import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, AlertTriangle, MessageSquare } from "lucide-react";
import { DashboardLayout } from "../common/page_component/index.js";
import ListPageHeader from "../common/ListPageHeader.js";
import ListFilterPanel from "../common/ListFilterPanel.js";
import Loader from "../common/Loader.js";
import Alert from "../common/Alert.js";
import RowActionsMenu from "../common/RowActionsMenu.js";
import { filterDateInputClass, listIdLinkClass, listIdLinkMobileClass, listPageWrapperClass } from "../common/uiClasses.js";
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import usePaginatedListFetch from "../../utils/usePaginatedListFetch.js";
import { formatIndianDate, formatIndianTime, formatTimeAgo } from "../../utils/localDate.js";
import { websiteService } from "../../services/websiteService.js";
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';

export const CASE_CONFIG = {
  enquiry: {
    title: "Service enquiries",
    icon: ClipboardList,
    listPath: "/website/enquiries",
    itemLabel: "enquiry",
    itemLabelPlural: "enquiries",
    searchPlaceholder: "Reference, name or mobile",
    detailLabel: "Route",
  },
  complaint: {
    title: "Complaints",
    icon: AlertTriangle,
    listPath: "/website/complaints",
    itemLabel: "complaint",
    itemLabelPlural: "complaints",
    searchPlaceholder: "Reference, LR number, name or mobile",
    detailLabel: "LR number",
  },
  message: {
    title: "Contact messages",
    icon: MessageSquare,
    listPath: "/website/messages",
    itemLabel: "message",
    itemLabelPlural: "messages",
    searchPlaceholder: "Reference, name or mobile",
    detailLabel: "Topic",
  },
};

const STATUS_OPTIONS = [
  { value: "open", label: "Registered" },
  { value: "in_progress", label: "Being looked into" },
  { value: "closed", label: "Resolved" },
];

export function statusClass(status) {
  if (status === "closed") return "bg-green-100 text-green-800";
  if (status === "in_progress") return "bg-blue-100 text-blue-800";
  return "bg-amber-100 text-amber-800";
}

export function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function detailLines(caseType, row) {
  if (caseType === "enquiry") {
    return [row.pickup || "—", row.delivery ? `to ${row.delivery}` : null].filter(Boolean);
  }
  if (caseType === "complaint") {
    return [row.lr_number || "—", row.booking_date || null, row.problem_type || null].filter(Boolean);
  }
  return [row.topic || "—"];
}

function statusText(row) {
  return String(row.status_label || row.status || "—").toUpperCase();
}

function DateCell({ value }) {
  return (
    <>
      <div className="whitespace-nowrap">{formatIndianDate(value)}</div>
      <div className="whitespace-nowrap">{formatIndianTime(value)}</div>
      {formatTimeAgo(value) ? (
        <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">{formatTimeAgo(value)}</div>
      ) : null}
    </>
  );
}

export default function WebsiteCaseList({ caseType }) {
  const config = CASE_CONFIG[caseType];
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const perPage = 10;

  const fetchPage = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await websiteService.listCases(caseType, {
        page: currentPage,
        per_page: perPage,
        search,
        status,
        from_date: fromDate,
        to_date: toDate,
      });
      setRows((prev) => mergeLoadedPage(currentPage, response.data.data || [], prev));
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      setRows([]);
      setError(err.response?.data?.error || "Could not load records.");
    } finally {
      setLoading(false);
    }
  }, [caseType, currentPage, search, status, fromDate, toDate]);

  usePaginatedListFetch({
    fetchPage,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, status, fromDate, toDate, caseType],
    debounceWhen: search,
    debounceMs: 0,
  });

  const openRecord = (row) => navigate(`${config.listPath}/view/${row.id}`);

  const changeStatus = async (row, nextStatus) => {
    try {
      await websiteService.updateCase(caseType, row.id, {
        status: nextStatus,
        internal_note: row.internal_note || "",
      });
      Alert.success("Status updated");
      fetchPage();
    } catch (err) {
      Alert.error(err.response?.data?.error || "Could not update the status");
    }
  };

  const actionItems = (row) =>
    STATUS_OPTIONS.filter((option) => option.value !== row.status).map((option) => ({
      label: `Mark as ${option.label.toLowerCase()}`,
      onClick: () => changeStatus(row, option.value),
    }));

  const hasActiveFilters = Boolean(search || status || fromDate || toDate);

  const renderActions = (row, menuId) => (
    <RowActionsMenu
      className="relative menu-container"
      isOpen={openMenuId === menuId}
      onToggle={() => setOpenMenuId(openMenuId === menuId ? null : menuId)}
      onClose={() => setOpenMenuId(null)}
      onView={() => openRecord(row)}
      editLabel="Update"
      onEdit={() => openRecord(row)}
      extraItems={actionItems(row)}
    />
  );

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
        <ListPageHeader title={config.title} icon={config.icon} className="mb-2 sm:mb-3" />

        <ListFilterPanel
          hasActiveFilters={hasActiveFilters}
          onClearFilters={() => {
            setSearch("");
            setStatus("");
            setFromDate("");
            setToDate("");
            setCurrentPage(1);
          }}
        >
          <div className="col-span-2 lg:col-span-1 min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Search</label>
            <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder={config.searchPlaceholder}
              inputClassName="list-filter-panel login-input w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base bg-white"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              className={filterDateInputClass}
              aria-label="From date"
            />
          </div>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              className={filterDateInputClass}
              aria-label="To date"
            />
          </div>
        </ListFilterPanel>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 w-full min-w-0 max-w-full">
          {loading && currentPage === 1 ? (
            <div className="flex items-center justify-center py-20">
              <Loader fullScreen={false} size="default" message={`Loading ${config.itemLabelPlural}...`} />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-600 text-lg font-semibold">{error}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔭</div>
              <p className="text-gray-600 text-lg font-semibold">No {config.itemLabelPlural} found</p>
              <p className="text-gray-500 mt-2">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="hidden xl:block w-full min-w-0">
                <table className="w-full table-fixed border-collapse [&_th]:min-w-0 [&_td]:min-w-0">
                  <thead className="bg-gray-50 rounded-t-xl">
                    <tr className="border-b border-gray-200">
                      <th className="w-[16%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Reference</th>
                      <th className="w-[22%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Customer</th>
                      <th className="w-[24%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{config.detailLabel}</th>
                      <th className="w-[16%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                      <th className="w-[14%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Date</th>
                      <th className="w-[8%] px-3 2xl:px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.map((row, index) => (
                      <tr
                        key={row.id}
                        className={`hover:bg-gray-50 relative transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                      >
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                          <a
                            href={`${config.listPath}/view/${row.id}`}
                            onClick={(event) => {
                              event.preventDefault();
                              openRecord(row);
                            }}
                            title={row.reference}
                            className={`${listIdLinkClass} break-words`}
                          >
                            {row.reference}
                          </a>
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                          <div className="font-regular text-gray-900 whitespace-normal break-words">{row.customer_name}</div>
                          <div className="font-regular text-gray-900 whitespace-normal break-words">{row.mobile}</div>
                          {row.email ? (
                            <div className="text-xs text-gray-400 mt-0.5 whitespace-normal break-words">{row.email}</div>
                          ) : null}
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top text-gray-900">
                          {detailLines(caseType, row).map((line, lineIndex) => (
                            <div key={lineIndex} className={lineIndex === 0 ? "whitespace-normal break-words" : "text-xs text-gray-500 mt-0.5 whitespace-normal break-words"}>
                              {line}
                            </div>
                          ))}
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top overflow-hidden">
                          <div className="uppercase text-gray-900 break-words leading-5">{statusText(row)}</div>
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top text-gray-900 leading-5">
                          <DateCell value={row.created_at} />
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm text-center align-top">
                          {renderActions(row, row.id)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="xl:hidden space-y-3 p-4">
                {rows.map((row) => (
                  <div key={`card-${row.id}`} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 border-l-4 border-l-blue-500">
                    <div className="mb-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <a
                            href={`${config.listPath}/view/${row.id}`}
                            onClick={(event) => {
                              event.preventDefault();
                              openRecord(row);
                            }}
                            className={`${listIdLinkMobileClass} block truncate`}
                          >
                            #{row.reference}
                          </a>
                          <p className="text-xs text-gray-400 mt-0.5 leading-4">
                            {formatIndianDate(row.created_at)}
                            {formatIndianTime(row.created_at) ? ` · ${formatIndianTime(row.created_at)}` : ""}
                            {formatTimeAgo(row.created_at) ? ` · ${formatTimeAgo(row.created_at)}` : ""}
                          </p>
                        </div>
                        {renderActions(row, `card-${row.id}`)}
                      </div>
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border border-gray-200 text-gray-800">
                        {statusText(row)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 font-medium">Customer</p>
                        <p className="font-semibold text-gray-800 break-words">{row.customer_name}</p>
                        <p className="text-[11px] text-gray-400 break-words">{row.mobile}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 font-medium">{config.detailLabel}</p>
                        {detailLines(caseType, row).map((line, lineIndex) => (
                          <p key={lineIndex} className={lineIndex === 0 ? "font-semibold text-gray-800 break-words" : "text-[11px] text-gray-400 break-words"}>
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

              </div>
              <ListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                loading={loading && currentPage > 1}
                onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
              />
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
